import React, { useState } from 'react';
import axios from 'axios';
import { consultaRuc, consultaDni } from '../utils/consultaPeru';
import {
  Box, Typography, Paper, TextField, Button, Checkbox, FormControlLabel, Grid, InputLabel, Select, MenuItem, FormControl, Divider, Avatar, IconButton, InputAdornment
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import SaveAsIcon from '@mui/icons-material/SaveAs';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import UbigeoAutocomplete from '../components/UbigeoAutocomplete';
import TravelExploreIcon from '@mui/icons-material/Explore';
import CircularProgress from '@mui/material/CircularProgress';

const initialState = {
  ruc: '',
  razonSocial: '',
  nombreComercial: '',
  direccion: '',
  ubigeo: '',
  telefono: '',
  email: '',
  logo: null,
  nombreVoucher: '',
  usuarioSol: '',
  claveSol: '',
  nombreFirma: '',
  claveFirma: '',
  usuarioGuia: '',
  claveGuia: '',
  clientId: '',
  clientSecret: '',
  ventasAfectasIgv: true,
  comprasAfectasIgv: true,
  porcentajeIgv: 18,
  porcentaje_igv_restaurante: 10.5,
  preciosIncluyenIgv: false,
  porcentajeDetraccion: '',
  cuentaDetraccion: '',
  agenteRetencion: false,
  agentePercepcion: false,
  bienesSelva: false,
  regimenRenta: '',
  regimenPercepcion: false,
  regimenRetencion: false,
  regimenDetraccion: false,
  incluyeIsc: false,
  porcentajeIsc: '',
  incluyeIcbper: false,
  montoIcbper: '',
  otrosTributos: '',
  emailEnvio: '',
  smtpServidor: '',
  smtpPuerto: '',
  smtpUsuario: '',
  smtpClave: '',
  smtpSsl: false,
  responsableLegal: '',
  dniResponsable: '',
  nombreContador: '',
  emailContador: '',
  celularContador: '',
  observaciones: ''
};

const regimenes = [
  { value: 'general', label: 'Régimen General' },
  { value: 'mype', label: 'MYPE Tributario' },
  { value: 'rus', label: 'RUS' },
  { value: 'especial', label: 'Especial' }
];

const ConfiguracionEmpresa = () => {
  const [modoProduccion, setModoProduccion] = useState(false);
  const [loadingModo, setLoadingModo] = useState(false);
  const [errorModo, setErrorModo] = useState('');

  const handleModoChange = async () => {
    if (loadingModo) return;
    setLoadingModo(true);
    setErrorModo('');
    const activandoProduccion = !modoProduccion;

    try {
      // 1. Actualizamos la Base de Datos para sincronizar el envío a SUNAT
      // Enviamos el booleano; el backend hará: req.body.modo_produccion ? 1 : 0
      await axios.put('/api/empresa-config/modo-produccion', { 
        modo_produccion: activandoProduccion 
      });

      // 2. Actualizamos los permisos de red (CORS) tal como estaba
      if (activandoProduccion) {
        await axios.post('/api/sistema/produccion');
      } else {
        await axios.delete('/api/sistema/produccion');
      }

      // 3. Actualizamos el estado visual
      setModoProduccion(activandoProduccion);
      alert(`Configuración actualizada: El sistema ahora operará en modo ${activandoProduccion ? 'PRODUCCIÓN' : 'PRUEBA (BETA)'}.`);

    } catch (err) {
      console.error('Error al cambiar modo:', err);
      const msg = err.response?.data?.error || 'No se pudo sincronizar el modo con el servidor.';
      setErrorModo(msg);
      alert('Error: ' + msg);
    } finally {
      setLoadingModo(false);
    }
  };
  const [form, setForm] = useState(initialState);
  const [firmaPreview, setFirmaPreview] = useState(null);
  const [firmaFile, setFirmaFile] = useState(null);
  const [ubigeoSeleccionado, setUbigeoSeleccionado] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [loadingSunat, setLoadingSunat] = useState(false);

  // Cargar configuración guardada al abrir la página
  React.useEffect(() => {
    async function cargarConfig() {
      try {
        const config = await require('../utils/empresaConfig').obtenerEmpresaConfig();
        if (config) {
          // Mapeo de campos snake_case a camelCase
          const mapped = {
            ruc: config.ruc || '',
            razonSocial: config.razon_social || '',
            nombreComercial: config.nombre_comercial || '',
            direccion: config.direccion || '',
            ubigeo: config.ubigeo || '',
            telefono: config.telefono || '',
            email: config.email || '',
            logo: null,
            nombreVoucher: config.nombre_voucher || '',
            usuarioSol: config.usuario_sol || '',
            claveSol: config.clave_sol || '',
            nombreFirma: config.nombre_firma || '',
            claveFirma: config.clave_firma || '',
            usuarioGuia: config.usuario_guia || '',
            claveGuia: config.clave_guia || '',
            clientId: config.client_id || '',
            clientSecret: config.client_secret || '',
            ventasAfectasIgv: config.ventas_afectas_igv === 1 || config.ventas_afectas_igv === true,
            comprasAfectasIgv: config.compras_afectas_igv === 1 || config.compras_afectas_igv === true,
            porcentajeIgv: config.porcentaje_igv !== null && config.porcentaje_igv !== undefined ? config.porcentaje_igv : '',
            porcentajeDetraccion: config.porcentaje_detraccion !== null && config.porcentaje_detraccion !== undefined ? config.porcentaje_detraccion : '',
            cuentaDetraccion: config.cuenta_detraccion || '',
            agenteRetencion: config.agente_retencion === 1 || config.agente_retencion === true,
            agentePercepcion: config.agente_percepcion === 1 || config.agente_percepcion === true,
            bienesSelva: config.bienes_selva === 1 || config.bienes_selva === true,
            regimenRenta: config.regimen_renta || '',
            regimenPercepcion: config.regimen_percepcion === 1 || config.regimen_percepcion === true,
            regimenRetencion: config.regimen_retencion === 1 || config.regimen_retencion === true,
            regimenDetraccion: config.regimen_detraccion === 1 || config.regimen_detraccion === true,
            incluyeIsc: config.incluye_isc === 1 || config.incluye_isc === true,
            porcentajeIsc: config.porcentaje_isc !== null && config.porcentaje_isc !== undefined ? config.porcentaje_isc : '',
            incluyeIcbper: config.incluye_icbper === 1 || config.incluye_icbper === true,
            montoIcbper: config.monto_icbper !== null && config.monto_icbper !== undefined ? config.monto_icbper : '',
            otrosTributos: config.otros_tributos || '',
            emailEnvio: config.email_envio || '',
            smtpServidor: config.smtp_servidor || '',
            smtpPuerto: config.smtp_puerto || '',
            smtpUsuario: config.smtp_usuario || '',
            smtpClave: config.smtp_clave || '',
            smtpSsl: config.smtp_ssl === 1 || config.smtp_ssl === true,
            responsableLegal: config.responsable_legal || '',
            dniResponsable: config.dni_responsable || '',
            nombreContador: config.nombre_contador || '',
            emailContador: config.email_contador || '',
            celularContador: config.celularContador || '',
            observaciones: config.observaciones || '',
            // NUEVO: preciosIncluyenIgv y porcentaje_igv_restaurante
            preciosIncluyenIgv: config.precios_incluyen_igv === 1 || config.precios_incluyen_igv === true,
            porcentaje_igv_restaurante: config.porcentaje_igv_restaurante !== null && config.porcentaje_igv_restaurante !== undefined ? Number(config.porcentaje_igv_restaurante) : 10.5
          };
          setForm(mapped);
          // Fuente de verdad real de si SUNAT esta en produccion o beta (antes
          // se inferia de si habia origenes CORS configurados, pero desde que
          // provision-empresa.sh registra el dominio propio de CADA empresa
          // nueva para que el login funcione, toda empresa recien creada
          // mostraba "Produccion Activada" aunque modo_produccion siguiera en 0).
          setModoProduccion(config.modo_produccion === 1 || config.modo_produccion === true);
          // Mostrar logo como imagen base64 si existe
          if (config.logo_base64) setLogoPreview(`data:image/*;base64,${config.logo_base64}`);
          else setLogoPreview(null);
          // Mostrar firma como botón de descarga si existe
          if (config.firma_base64) setFirmaPreview({
            url: URL.createObjectURL(new Blob([Uint8Array.from(atob(config.firma_base64), c => c.charCodeAt(0))], { type: 'application/x-pkcs12' })),
            name: config.nombre_firma || 'firma.pfx'
          });
          else setFirmaPreview(null);

          // --- Cargar objeto ubigeo si hay código guardado ---
          if (config.ubigeo) {
            if (typeof config.ubigeo === 'object' && config.ubigeo.codigo) {
              setUbigeoSeleccionado(config.ubigeo);
            } else {
              const { obtenerUbigeoPorCodigo } = require('../utils/ubigeo');
              obtenerUbigeoPorCodigo(config.ubigeo).then(objUbigeo => {
                setUbigeoSeleccionado(objUbigeo);
              });
            }
          } else {
            setUbigeoSeleccionado(null);
          }
        }
      } catch (err) {
        // No mostrar error si no hay config
      }
    }
    cargarConfig();
  }, []);
  const [loadingRuc, setLoadingRuc] = useState(false);
  const [loadingDni, setLoadingDni] = useState(false);
  const handleConsultaRuc = async () => {
    if (!form.ruc || form.ruc.length < 8) return;
    setLoadingRuc(true);
    const data = await consultaRuc(form.ruc);
    setLoadingRuc(false);
    if (data && data.razonSocial) {
      setForm(prev => ({
        ...prev,
        razonSocial: data.razonSocial || '',
        direccion: data.direccion || '',
        ubigeo: data.ubigeo || '',
        telefono: data.telefono || prev.telefono,
        email: data.email || prev.email
      }));
    } else {
      alert('No se encontraron datos para el RUC ingresado.');
    }
  };

  const handleConsultaDni = async () => {
    if (!form.dniResponsable || form.dniResponsable.length !== 8) return;
    setLoadingDni(true);
    const data = await consultaDni(form.dniResponsable);
    setLoadingDni(false);
    if (data && data.nombres) {
      setForm(prev => ({
        ...prev,
        responsableLegal: `${data.nombres} ${data.apellidoPaterno} ${data.apellidoMaterno}`.trim()
      }));
    } else {
      alert('No se encontraron datos para el DNI ingresado.');
    }
  };

  const handleChange = e => {
    const { name, value, type, checked } = e.target;
    setForm(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleLogo = e => {
    const file = e.target.files[0];
    if (file) {
      // Solo aceptar png, jpg, jpeg (ICO no es compatible con generación de PDF)
      const allowed = ['image/png', 'image/jpeg', 'image/jpg'];
      const ext = file.name.substring(file.name.lastIndexOf('.')).toLowerCase();
      const allowedExt = ['.png', '.jpg', '.jpeg'];
      if (!allowed.includes(file.type) && !allowedExt.includes(ext)) {
        alert('Formato de logo no permitido. Solo PNG, JPG o JPEG.\n\nNota: Los archivos ICO no son compatibles con la generación de PDF.');
        return;
      }
      setForm(prev => ({ ...prev, logo: file, logoNombre: file.name }));
      setLogoPreview(URL.createObjectURL(file));
    }
  };

  const handleSubmit = async e => {
    e.preventDefault();
    try {
      // Mapeo snake_case para booleanos y mantener logo/firma si no hay archivo nuevo
      const data = { ...form };
      data.porcentajeIgv = form.porcentajeIgv ? Number(form.porcentajeIgv) : 0;
      data.porcentajeDetraccion = form.porcentajeDetraccion ? Number(form.porcentajeDetraccion) : 0;
      data.porcentajeIsc = form.porcentajeIsc ? Number(form.porcentajeIsc) : 0;
      data.montoIcbper = form.montoIcbper ? Number(form.montoIcbper) : 0;
      // Booleanos en snake_case
      const boolMap = {
        ventasAfectasIgv: 'ventas_afectas_igv',
        comprasAfectasIgv: 'compras_afectas_igv',
        agenteRetencion: 'agente_retencion',
        agentePercepcion: 'agente_percepcion',
        bienesSelva: 'bienes_selva',
        regimenPercepcion: 'regimen_percepcion',
        regimenRetencion: 'regimen_retencion',
        regimenDetraccion: 'regimen_detraccion',
        incluyeIsc: 'incluye_isc',
        incluyeIcbper: 'incluye_icbper',
        smtpSsl: 'smtp_ssl',
        preciosIncluyenIgv: 'precios_incluyen_igv' // NUEVO camelCase
      };
      Object.keys(boolMap).forEach(key => {
        data[boolMap[key]] = form[key] ? 1 : 0;
        delete data[key];
      });
      // Guardar porcentaje_igv_restaurante en snake_case y como número
      if (form.porcentaje_igv_restaurante !== undefined) {
        data.porcentaje_igv_restaurante = Number(form.porcentaje_igv_restaurante);
      }
      if (!data.ruc || data.ruc === '') {
        alert('El campo RUC es obligatorio.');
        return;
      }
      // Mantener logo/firma si no hay archivo nuevo
      if (form.logo) {
        data.logo = form.logo;
        data.logoNombre = form.logo.name;
      } else {
        delete data.logo;
        delete data.logoNombre;
        delete data.logo_base64;
      }
      if (firmaFile) {
        data.firma = firmaFile;
        data.nombreFirma = firmaFile.name;
      } else {
        delete data.firma;
        delete data.nombreFirma;
        delete data.firma_base64;
      }
      await require('../utils/empresaConfig').guardarEmpresaConfig(data);
      alert('Configuración guardada correctamente');
    } catch (err) {
      const msg = err?.response?.data?.error || err?.message || 'Error desconocido';
      alert('Error al guardar configuración: ' + msg);
    }
  };

    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mt: 1, mb: 4, width: '100%' }}>
        <Box sx={{ width: { xs: '100%', sm: '95%', md: '90%' }, maxWidth: 1600 }}>
          <Typography variant="h4" gutterBottom sx={{ textAlign: { xs: 'center', md: 'left' }, mb: 3, fontWeight: 'bold', color: '#222' }}>
            Configuración de Empresa
          </Typography>
          <Paper sx={{ width: '100%', mx: 'auto', p: { xs: 2, sm: 4 }, borderRadius: 4, boxShadow: '0 4px 24px rgba(0,0,0,0.08)' }}>
            <form onSubmit={handleSubmit}>
              <Typography variant="h6" fontWeight="bold" sx={{ color: '#1976d2', mb: 1, textAlign: 'left' }}>Datos Generales</Typography>
              <Divider sx={{ mb: 3 }} />
              <Grid container spacing={2}  alignItems="center" justifyContent="center">
                {/* Fila 1: RUC, Razón Social, Nombre Comercial, Dirección Fiscal */}
                <Grid item xs={12} md={1}>
                  <TextField
                    label="RUC"
                    name="ruc"
                    value={form.ruc}
                    onChange={handleChange}
                    fullWidth
                    required
                    size="small"
                    sx={{ minWidth: '15em' }}
                    slotProps={{
                      input: {
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              onClick={handleConsultaRuc}
                              disabled={loadingRuc || !form.ruc}
                              edge="end"
                              size="small"
                              title="Buscar SUNAT/RENIEC"
                            >
                              {loadingSunat ? <CircularProgress size={20} /> : <TravelExploreIcon />}
                            </IconButton>
                          </InputAdornment>
                        )
                      }
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField label="Razón Social" name="razonSocial" value={form.razonSocial} onChange={handleChange} fullWidth required size="small" sx={{ minWidth: '45em' }} />
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField label="Nombre Comercial" name="nombreComercial" value={form.nombreComercial} onChange={handleChange} fullWidth size="small" sx={{ minWidth: '35em' }} />
                </Grid>
              </Grid>
              <Grid container spacing={2} alignItems="center" justifyContent="center" sx={{mt: 2}}>
                <Grid item xs={12} md={3}>
                  <TextField label="Dirección Fiscal" name="direccion" value={form.direccion} onChange={handleChange} fullWidth size="small" sx={{ minWidth: '50em' }} />
                </Grid>
                <Grid item xs={12} md={6}>
                  <UbigeoAutocomplete
                    value={ubigeoSeleccionado}
                    onChange={ubigeo => {
                      setUbigeoSeleccionado(ubigeo);
                      setForm(prev => ({ ...prev, ubigeo: ubigeo ? ubigeo.codigo : '' }));
                    }}
                    label="Ubigeo"
                    fullWidth
                    size="small"
                    sx={{ minWidth: '40em' }}
                  />
                </Grid>
              </Grid>
              <Grid container spacing={2} alignItems="center" justifyContent="center" sx={{mt: 2}}>
                <Grid item xs={12} md={2}>
                  <TextField label="Teléfono/Celular" name="telefono" value={form.telefono} onChange={handleChange} fullWidth size="small" sx={{ minWidth: '10em' }} />
                </Grid>
                <Grid item xs={12} md={2}>
                  <TextField label="Email principal" name="email" value={form.email} onChange={handleChange} fullWidth size="small" sx={{ minWidth: '30em' }} />
                </Grid>
                <Grid item xs={12} md={2}>
                  <TextField label="Nombre en voucher/ticket" name="nombreVoucher" value={form.nombreVoucher} onChange={handleChange} fullWidth size="small" sx={{ minWidth: '17.5em' }} />
                </Grid>
              </Grid>
              <Grid container spacing={2} justifyContent="center" sx={{mt: 2}}>
                <Grid item xs={12} sm={6} md={3}>
                  <Button component="label" variant="outlined" startIcon={<CloudUploadIcon />} fullWidth size="small" sx={{ minWidth: '25em' }}>
                    Subir Logo
                    <input type="file" accept="image/*" hidden onChange={handleLogo} />
                  </Button>
                  {logoPreview && (
                    <Box sx={{ mt: 1, textAlign: 'center' }}>
                      <Typography variant="body2">Logo actual:</Typography>
                      <Avatar src={logoPreview} alt="Logo" sx={{ width: 48, height: 48, mt: 1, mx: 'auto' }} />
                      <a href={logoPreview} download="logo.png" target="_blank" rel="noopener noreferrer" style={{ display: 'block', margin: '0 auto' }}>
                        Descargar Logo
                      </a>
                    </Box>
                  )}
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Button component="label" variant="outlined" startIcon={<CloudUploadIcon />} fullWidth size="small" sx={{ minWidth: '25em' }}>
                    Subir Firma Electrónica PFX
                    <input type="file" accept=".pfx" hidden onChange={e => {
                      setFirmaFile(e.target.files[0]);
                      setFirmaPreview(e.target.files[0]?.name || null);
                    }} />
                  </Button>
                  {firmaPreview && (
                    <Box sx={{ mt: 1, textAlign: 'center' }}>
                      <Typography variant="body2">Firma actual:</Typography>
                      <a href={firmaPreview.url} download={firmaPreview.name} target="_blank" rel="noopener noreferrer" style={{ display: 'block', margin: '0 auto' }}>
                        Descargar Firma Electrónica (.pfx)
                      </a>
                    </Box>
                  )}
                </Grid>
              </Grid>
              <Typography variant="h6" fontWeight="bold" sx={{ color: '#1976d2', mt: 6, mb: 1, textAlign: 'left' }}>Configuración SUNAT</Typography>
              <Divider sx={{ mb: 3 }} />
              <Grid container spacing={2} alignItems="center" justifyContent="center">
                <Grid item xs={12} sm={4} md={3}>
                  <TextField label="Usuario SOL secundario" name="usuarioSol" value={form.usuarioSol} onChange={handleChange} fullWidth size="small"  sx={{ minWidth: '16.5em' }} />
                </Grid>
                <Grid item xs={12} sm={4} md={3}>
                  <TextField label="Clave SOL secundaria" name="claveSol" value={form.claveSol} onChange={handleChange} fullWidth type="password" size="small" sx={{ minWidth: '16.5em' }} />
                </Grid>
                <Grid item xs={12} sm={4} md={3}>
                  <TextField label="Nombre de firma electrónica" name="nombreFirma" value={form.nombreFirma} onChange={handleChange} fullWidth size="small" sx={{ minWidth: '16.5em' }} />
                </Grid>
                <Grid item xs={12} sm={4} md={3}>
                  <TextField label="Clave de firma electrónica" name="claveFirma" value={form.claveFirma} onChange={handleChange} fullWidth type="password" size="small" sx={{ minWidth: '16.5em' }} />
                </Grid>
              </Grid>
              <Grid container spacing={2} alignItems="center" justifyContent="center" sx={{mt: 2}}>
                <Grid item xs={12} sm={4} md={3}>
                  <TextField label="Usuario guía de remisión" name="usuarioGuia" value={form.usuarioGuia} onChange={handleChange} fullWidth size="small" sx={{ minWidth: '16.5em' }} />
                </Grid>
                <Grid item xs={12} sm={4} md={3}>
                  <TextField label="Clave SOL guía de remisión" name="claveGuia" value={form.claveGuia} onChange={handleChange} fullWidth type="password" size="small" sx={{ minWidth: '16.5em' }} />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField label="Client ID (API SUNAT)" name="clientId" value={form.clientId} onChange={handleChange} fullWidth size="small" sx={{ minWidth: '20em' }} />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField label="Client Secret (API SUNAT)" name="clientSecret" value={form.clientSecret} onChange={handleChange} fullWidth size="small" sx={{ minWidth: '33em' }} />
                </Grid>
              </Grid>
              <Typography variant="h6" fontWeight="bold" sx={{ color: '#1976d2', mt: 6, mb: 1, textAlign: 'left' }}>Parámetros Tributarios y Comprobantes</Typography>
              <Divider sx={{ mb: 2 }} />
              <Grid container spacing={2} alignItems="center" justifyContent="center">
                <Grid item xs={12} sm={3} md={2}>
                  <FormControlLabel control={<Checkbox checked={form.ventasAfectasIgv} onChange={handleChange} name="ventasAfectasIgv" />} label="Ventas afectas a IGV" sx={{ ml: 0, minWidth: '15em' }} />
                </Grid>
                <Grid item xs={12} sm={3} md={2}>
                  <FormControlLabel control={<Checkbox checked={form.comprasAfectasIgv} onChange={handleChange} name="comprasAfectasIgv" />} label="Compras afectas a IGV" sx={{ ml: 0, minWidth: '15em' }} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={form.preciosIncluyenIgv}
                        onChange={handleChange}
                        color="primary"
                        name="preciosIncluyenIgv"
                      />
                    }
                    label="Precios incluyen IGV por defecto"
                  />
                </Grid>
                <Grid item xs={12} sm={6} sx={{ minWidth: '18em' }}>
                  <TextField
                    label="Porcentaje IGV Restaurantes"
                    type="number"
                    inputProps={{ min: 0, max: 100, step: 0.01 }}
                    value={form.porcentaje_igv_restaurante || ''}
                    onChange={e => setForm({ ...form, porcentaje_igv_restaurante: e.target.value })}
                    fullWidth
                  />
                </Grid>
              </Grid>
              <Grid container spacing={2} alignItems="center" justifyContent="center" sx={{mt: 2}}>
                <Grid item xs={12} sm={3} md={2}>
                  <TextField label="Porcentaje IGV" name="porcentajeIgv" value={form.porcentajeIgv} onChange={handleChange} fullWidth type="number" size="small" sx={{ minWidth: '15em' }} />
                </Grid>
                <Grid item xs={12} sm={6} md={2}>
                  <FormControl fullWidth size="small" sx={{ minWidth: '14.3em' }}>
                    <InputLabel>Régimen de renta</InputLabel>
                    <Select name="regimenRenta" value={form.regimenRenta} onChange={handleChange} label="Régimen de renta">
                      {regimenes.map(r => <MenuItem key={r.value} value={r.value}>{r.label}</MenuItem>)}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6} md={2}>
                  <FormControlLabel control={<Checkbox checked={form.regimenPercepcion} onChange={handleChange} name="regimenPercepcion" />} label="Régimen percepción" sx={{ ml: 0, minWidth: '15em' }} />
                </Grid>
                <Grid item xs={12} sm={6} md={2}>
                  <FormControlLabel control={<Checkbox checked={form.regimenRetencion} onChange={handleChange} name="regimenRetencion" />} label="Régimen retención" sx={{ ml: 0, minWidth: '15em' }} />
                </Grid>
                <Grid item xs={12} sm={6} md={2}>
                  <FormControlLabel control={<Checkbox checked={form.regimenDetraccion} onChange={handleChange} name="regimenDetraccion" />} label="Régimen detracción" sx={{ ml: 0, minWidth: '15em' }} />
                </Grid>
              </Grid>
              <Grid container spacing={2} alignItems="center" justifyContent="center" sx={{mt: 2}}>
                <Grid item xs={12} sm={3} md={2}>
                  <TextField label="Porcentaje detracción" name="porcentajeDetraccion" value={form.porcentajeDetraccion} onChange={handleChange} fullWidth type="number" size="small" sx={{ minWidth: '15em' }} />
                </Grid>
                <Grid item xs={12} sm={6} md={2}>
                  <TextField label="N° cuenta detracción" name="cuentaDetraccion" value={form.cuentaDetraccion} onChange={handleChange} fullWidth size="small" sx={{ minWidth: '15em' }} />
                </Grid>
                <Grid item xs={12} sm={6} md={2}>
                  <FormControlLabel control={<Checkbox checked={form.agenteRetencion} onChange={handleChange} name="agenteRetencion" />} label="Agente de retención" sx={{ ml: 0, minWidth: '15em' }} />
                </Grid>
                <Grid item xs={12} sm={6} md={2}>
                  <FormControlLabel control={<Checkbox checked={form.agentePercepcion} onChange={handleChange} name="agentePercepcion" />} label="Agente de percepción" sx={{ ml: 0, minWidth: '15em' }} />
                </Grid>
                <Grid item xs={12} sm={6} md={2}>
                  <FormControlLabel control={<Checkbox checked={form.bienesSelva} onChange={handleChange} name="bienesSelva" />} label="Bienes para la selva" sx={{ ml: 0, minWidth: '15em' }} />
                </Grid>
              </Grid>
              <Grid container spacing={2} alignItems="center" justifyContent="center" sx={{mt: 2}}>
                <Grid item xs={12} sm={6} md={2}>
                  <FormControlLabel control={<Checkbox checked={form.incluyeIsc} onChange={handleChange} name="incluyeIsc" />} label="Incluye ISC" sx={{ ml: 0 }} />
                </Grid>
                <Grid item xs={12} sm={6} md={2}>
                  <TextField label="% ISC" name="porcentajeIsc" value={form.porcentajeIsc} onChange={handleChange} fullWidth type="number" disabled={!form.incluyeIsc} size="small" sx={{ minWidth: '15em' }} />
                </Grid>
                <Grid item xs={12} sm={6} md={2}>
                  <FormControlLabel control={<Checkbox checked={form.incluyeIcbper} onChange={handleChange} name="incluyeIcbper" />} label="Incluye ICBPER" sx={{ ml: 0 }} />
                </Grid>
                <Grid item xs={12} sm={6} md={2}>
                  <TextField label="Monto ICBPER" name="montoIcbper" value={form.montoIcbper} onChange={handleChange} fullWidth type="number" disabled={!form.incluyeIcbper} size="small" sx={{ minWidth: '15em' }} />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <TextField label="Otros tributos" name="otrosTributos" value={form.otrosTributos} onChange={handleChange} fullWidth size="small" sx={{ minWidth: '15em' }} />
                </Grid>
              </Grid>
              <Typography variant="h6" fontWeight="bold" sx={{ color: '#1976d2', mt: 6, mb: 1, textAlign: 'left' }}>Configuración Email</Typography>
              <Divider sx={{ mb: 3 }} />
              <Grid container spacing={2} alignItems="center" justifyContent="center">
                <Grid item xs={12} sm={4} md={3}>
                  <TextField label="Email envío comprobantes" name="emailEnvio" value={form.emailEnvio} onChange={handleChange} fullWidth size="small" sx={{ minWidth: '25em' }} />
                </Grid>
                <Grid item xs={12} sm={2} md={2}>
                  <TextField label="SMTP Servidor" name="smtpServidor" value={form.smtpServidor} onChange={handleChange} fullWidth size="small" />
                </Grid>
                <Grid item xs={12} sm={2} md={2}>
                  <TextField label="SMTP Puerto" name="smtpPuerto" value={form.smtpPuerto} onChange={handleChange} fullWidth size="small" />
                </Grid>
                <Grid item xs={12} sm={2} md={2}>
                  <TextField label="SMTP Usuario" name="smtpUsuario" value={form.smtpUsuario} onChange={handleChange} fullWidth size="small" />
                </Grid>
                <Grid item xs={12} sm={2} md={2}>
                  <TextField label="SMTP Clave" name="smtpClave" value={form.smtpClave} onChange={handleChange} fullWidth type="password" size="small" />
                </Grid>
                <Grid item xs={12} sm={2} md={1}>
                  <FormControlLabel control={<Checkbox checked={form.smtpSsl} onChange={handleChange} name="smtpSsl" />} label="Usar SSL/TLS" sx={{ ml: 0 }} />
                </Grid>
              </Grid>
              <Typography variant="h6" fontWeight="bold" sx={{ color: '#1976d2', mt: 6, mb: 1, textAlign: 'left' }}>Otros Datos</Typography>
              <Divider sx={{ mb: 3 }} />
              <Grid container spacing={2} alignItems="center" justifyContent="center">
                <Grid item xs={12} md={1}>
                  <TextField
                    label="DNI responsable"
                    name="dniResponsable"
                    value={form.dniResponsable}
                    onChange={handleChange}
                    fullWidth
                    required
                    size="small"
                    sx={{ minWidth: '15em' }}
                    slotProps={{
                      input: {
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              onClick={handleConsultaDni}
                              disabled={loadingDni || !form.dniResponsable}
                              sx={{ ml: 0, minWidth: 0 }}
                              edge="end"
                              size="small"
                              title="Buscar SUNAT/RENIEC"
                            >
                              {loadingDni ? <CircularProgress size={20} /> : <TravelExploreIcon />}
                            </IconButton>
                          </InputAdornment>
                        )
                      }
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={4} md={3}>
                  <TextField label="Responsable legal" name="responsableLegal" value={form.responsableLegal} onChange={handleChange} fullWidth size="small" sx={{ minWidth: '20em' }} />
                </Grid>
                <Grid item xs={12} sm={4} md={3}>
                  <TextField label="Nombre contador" name="nombreContador" value={form.nombreContador} onChange={handleChange} fullWidth size="small" sx={{ minWidth: '15em' }} />
                </Grid>
                <Grid item xs={12} sm={2} md={2}>
                  <TextField label="Email contador" name="emailContador" value={form.emailContador} onChange={handleChange} fullWidth size="small" sx={{ minWidth: '20em' }} />
                </Grid>
                <Grid item xs={12} sm={2} md={2}>
                  <TextField label="Celular contador" name="celularContador" value={form.celularContador} onChange={handleChange} fullWidth size="small" />
                </Grid>
              </Grid>
              <Grid container spacing={2} alignItems="center" justifyContent="center" sx={{mt: 2}}>
                <Grid item xs={12} sm={10} md={10}>
                  <TextField label="Observaciones / Configuración adicional" name="observaciones" value={form.observaciones} onChange={handleChange} fullWidth multiline rows={1} size="small" sx={{ minWidth: '90em' }} />
                </Grid>
              </Grid>
              <Grid container spacing={2} alignItems="center" justifyContent="center" sx={{mt: 2}}>
                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3, gap: 2 }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                      <Button
                        variant="contained"
                        color={modoProduccion ? 'success' : 'warning'}
                        onClick={handleModoChange}
                        disabled={loadingModo}
                        startIcon={loadingModo ? <CircularProgress size={18} color="inherit" /> : modoProduccion ? <VerifiedUserIcon /> : <WarningAmberIcon />}
                        size="large"
                      >
                        {modoProduccion ? 'Modo Producción Activado' : 'Modo Prueba (Click para activar producción)'}
                      </Button>
                      {errorModo && <Typography variant="caption" color="error">{errorModo}</Typography>}
                    </Box>
                    <Button
                      type="submit"
                      variant="contained"
                      color="primary"
                      size="large"
                      startIcon={<SaveAsIcon />}
                    >
                      Guardar Configuración
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            </form>
          </Paper>
        </Box>
      </Box>
    );
};

export default ConfiguracionEmpresa;
